import type { ReactNode } from 'react';
import type { DefaultTheme, ExecutionContext } from 'styled-components';

import { css, styled } from 'styled-components';

export type BaseProps = {
  bottom?: number;
  left?: number;
  right?: number;
  top?: number;
  unit?: 'px' | 'rem' | 'em' | 'vw' | 'vh';
};

export type BaseTransientProps = {
  [key in keyof BaseProps as `$${key}`]?: BaseProps[key];
};

export type ResponsiveProps = {
  [key in keyof DefaultTheme['devices'] as `$${key}`]?: BaseProps;
};

export type SpacerProps = BaseTransientProps & ResponsiveProps & { children: ReactNode };

const renderBasePropsStyles = (props?: BaseTransientProps) => {
  const { $bottom, $left, $right, $top, $unit = 'rem' } = props || {};

  return css`
    ${$top !== undefined && `padding-top: ${$top}${$unit};`}
    ${$right !== undefined && `padding-right: ${$right}${$unit};`}
    ${$bottom !== undefined && `padding-bottom: ${$bottom}${$unit};`}
    ${$left !== undefined && `padding-left: ${$left}${$unit};`}
  `;
};

const addPrefixToBaseProps = (props: BaseProps) => {
  return Object.entries(props).reduce((acc, [key, value]) => {
    return {
      ...acc,
      [`$${key}`]: value,
    };
  }, {});
};

const getResponsiveProps = (props: ExecutionContext & SpacerProps) => {
  return Object.entries(props).map(([key, value]) => {
    const keyWithoutPrefix = key.replace('$', '');

    if (keyWithoutPrefix in props.theme.devices) {
      return css`
        @media (min-width: ${props.theme.devices[keyWithoutPrefix as keyof DefaultTheme['devices']]}) {
          ${renderBasePropsStyles(addPrefixToBaseProps(value))}
        }
      `;
    }

    return css``;
  });
};

export const Spacer = styled.div<SpacerProps>`
  width: 100%;

  ${(props) => renderBasePropsStyles(props)}

  ${(props) => getResponsiveProps(props)}
`;
